import { ChangeEvent, useState, useEffect, useRef, Fragment } from 'react';
import {
  Divider,
  Box,
  Card,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Typography,
  Grid,
  Button
} from '@mui/material';

import { ResourcePoolFilter } from 'src/models/employee';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useDispatch, useSelector } from 'react-redux';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { useForm } from 'react-hook-form';
import './ResourcePool.css';
import { withStyles } from '@mui/styles';
import { TableColumnCell } from 'src/components/TableColumnCell';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import moment from 'moment';
import { AppActions } from 'src/store/AppReducer';
import ProjectMultiSelect from './common/ProjectMultiSelect';
import ResourceTypeSelect from './common/ResourceTypeSelect';
import ResourceMultiSelect from './common/ResourceMultiSelect';
import NoRecord from './NoRecord';
import SingleDatePicker from './common/SingleDatePicker';
import { downloadProjectsReport, getProjects } from 'src/services/ResourcePoolService';
import AllocationRoleMultiSelect from './common/AllocationRoleMultiSelect';
import fileDownload from 'js-file-download';
import SnackBar from 'src/components/SnackBar/SnackBar';
import GetAppIcon from '@mui/icons-material/GetApp';
import { RootStateType } from 'src/store/RootReducer';

const ProjectTable = () => {
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer.user);

  const [expand, setExpand] = useState<any>();
  const [toggleFilter, setToggleFilter] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isClearDisabled, setIsClearDisabled] = useState(false);
  const [resources, setResources] = useState<any>([]);
  const [total, setTotal] = useState<number>(0);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [isFilterChanged, setIsFilterChanged] = useState(true);

  const pagination_wrapper_ref = useRef(null);

  let initialResourceFilterValues = {
    resourceType: null,
    project: null,
    resource: null,
    allocationStartDate: null,
    allocationEndDate: null,
    allocationRoles:null,
    limit: 10,
    offset: 0,
    isSubmitted: true
  } as ResourcePoolFilter;
  const [resourcePoolFilter, setResourcePoolFilter] = useState(initialResourceFilterValues as ResourcePoolFilter);
  const [prevResourcePoolFilter, setPrevResourcePoolFilter] = useState(initialResourceFilterValues as ResourcePoolFilter);


  const { register, handleSubmit, reset, setValue, watch } =
    useForm<ResourcePoolFilter>({
      defaultValues: initialResourceFilterValues
    });

  const startDate = watch('allocationStartDate');

  const isEndDateValid = (endDate, startDate) => {
    return new Date(endDate) >= new Date(startDate) || (endDate == null && startDate == null);
  };

  const handlePageChange = (event: any, newPage: number): void => {
    const validate = isEndDateValid(prevResourcePoolFilter.allocationEndDate, prevResourcePoolFilter.allocationStartDate);
    setResourcePoolFilter({
      ...prevResourcePoolFilter,
      offset: newPage,
      limit: resourcePoolFilter.limit,
      isSubmitted: true,
      allocationStartDate: validate ? prevResourcePoolFilter.allocationStartDate : null
    });
    setExpand({});
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const validate = isEndDateValid(prevResourcePoolFilter.allocationEndDate, prevResourcePoolFilter.allocationStartDate);
    setResourcePoolFilter({
      ...prevResourcePoolFilter,
      limit: parseInt(event.target.value),
      isSubmitted: true,
      allocationStartDate: validate ? prevResourcePoolFilter.allocationStartDate : null
    });
    setExpand({});
  };

  const onSubmit = () => {
    const filter = {
      ...resourcePoolFilter,
      isSubmitted: true,
      offset: 0,
      limit: 10
    }

    if(isFilterChanged){
      setResourcePoolFilter(filter);
      setPrevResourcePoolFilter(filter);
      setIsFilterChanged(false);
    }  
    setExpand({});  
  };

  useEffect(() => {
    if(resourcePoolFilter.isSubmitted){
      dispatch(AppActions.showLoader());
      const payload = {
        ...resourcePoolFilter,
        allocationStartDate: resourcePoolFilter.allocationStartDate ? moment(resourcePoolFilter.allocationStartDate).format('MM/DD/YYYY') : moment(new Date()).format('MM/DD/YYYY'),
        allocationEndDate: resourcePoolFilter.allocationEndDate ? moment(resourcePoolFilter.allocationEndDate).format('MM/DD/YYYY') :  moment(new Date()).format('MM/DD/YYYY'),
        offset: resourcePoolFilter.offset * resourcePoolFilter.limit
      };

      const fetchAllResources = (payload) => {
        //setResources(dummy_resources);
        getProjects(payload).then((res) => {
          const data = res.data
          setResources(data.data);
          setTotal(data.total)
          dispatch(AppActions.hideLoader());
        });
      };
  
      fetchAllResources(payload);
      setResourcePoolFilter((prev) => ({
        ...prev,
        isSubmitted: false
      }))
    }
  }, [resourcePoolFilter]);

  //clear the filter
  const resetFilterResult = () => {
    reset();

    const result = compareObjects(initialResourceFilterValues, resourcePoolFilter, "isSubmitted");
    !result && setResourcePoolFilter({
      ...initialResourceFilterValues,
      allocationEndDate: null,
      allocationStartDate: null
    });

    setPrevResourcePoolFilter({
      ...initialResourceFilterValues,
      allocationEndDate: null,
      allocationStartDate: null
    });
  };

  useEffect(() => {
    const filter = {
      allocationEndDate: resourcePoolFilter.allocationEndDate,
      allocationStartDate: resourcePoolFilter.allocationStartDate,
      project: resourcePoolFilter.project,
      resource: resourcePoolFilter.resource,
      resourceType: resourcePoolFilter.resourceType,
      allocationRoles: resourcePoolFilter.allocationRoles,
    };

    const isNullish = Object.values(filter).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(filter).length === 0 || isNullish) {
      setIsDisabled(true);
      setIsClearDisabled(true);
    } else {
      if(resourcePoolFilter.allocationStartDate && resourcePoolFilter.allocationEndDate == null){
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
      setIsClearDisabled(false);
    }
  }, [resourcePoolFilter]);



  const handleExpand = (id) => {
    setExpand(
      {
        id: id,
        expand: expand?.id === id ? !expand.expand : true
      }
    );
  }

  const getMinimumDate = (Arr) => {
    if(Arr.length > 0){
      // Convert date strings to Date objects
      const dateArr =Arr.map(e => e.allocationStartDate);
      const dateObjects = dateArr.map(dateString => new Date(dateString)); 
      const minDate = new Date(Math.min.apply(null, dateObjects));
      return moment(minDate).format(
        'YYYY-MM-DD'
      ) || '-'
    } else {
      return '-';
    }
  }

  const getMaximumDate = (Arr) => {
    if(Arr.length > 0){
      // Convert date strings to Date objects
      const dateArr =Arr.map(e => e.allocationEndDate)
      const dateObjects = dateArr.map(dateString => new Date(dateString)); 
      const maxDate = new Date(Math.max.apply(null, dateObjects));
      return moment(maxDate).format(
        'YYYY-MM-DD'
      ) || '-'
    } else {
      return '-';
    }
  }

  const compareObjects = (obj1, obj2, excludedProperty) => {
    // Get the keys of the objects
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
  
    // Check if the number of keys is the same
    if (keys1.length !== keys2.length) {
      return false;
    }
  
    // Iterate over the keys and compare the values
    for (let key of keys1) {
      // Skip the excluded property
      if (key === excludedProperty) {
        continue;
      }
  
      // Check if the values are not equal
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }
  
    // If all checks passed, the objects are equal
    return true;
  }

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);

  const getProjectTotalAllocation = (resources) => {
    const sumByAllocation: { [key: string]: number } = resources.reduce((acc, obj) => {
      acc[obj.name] = (acc[obj.name] || 0) + obj.allocation;
      return acc;
    }, {});

    const result = Object.entries(sumByAllocation).map(([name, allocation]) => ({
      name,
      allocation
    }));

    if(result?.length > 0){
      const count_100 = result?.filter(e => e.allocation > 75).length;
      const count_75 = result?.filter(e => e.allocation <= 75 && e.allocation > 50).length;
      const count_50 = result?.filter(e => e.allocation <= 50 && e.allocation > 25).length;
      const count_25 = result?.filter(e => e.allocation <= 25 && e.allocation > 0).length;

      return count_100 + " / " + count_75 + " / " + count_50 + " / " + count_25;
    } else {
      return "0 / 0 / 0 / 0";
    }
  }

  const getRole = (resources) => {
    let role = '';
    if(resources?.length > 1){
      const allEqual = resources?.every(val => val.role === resources[0]?.role);
      role = allEqual ? resources[0]?.role : "Multiple Roles";
    } else if(resources?.length === 1){
      role = resources[0]?.role;
    } else if(resources?.length === 0){
      role = "-";
    }
    return role;
  }

  const getResource = (resources) => {
    let name = '';

    const uniqueArr = Array.from(new Set(resources.map(a => a.name)))
    .map(name => {
      return resources.find(a => a.name === name);
    });

    if(resources?.length > 1){
      const allEqual = resources?.every(val => val.name === resources[0]?.name);
      name = allEqual ? resources[0]?.name : uniqueArr.length + " Resources";
    } else if(resources?.length === 1){
      name = resources[0]?.name;
    } else if(resources?.length === 0){
      name = "-"
    }
    return name;
  }

  const handleDownload = () => {
    dispatch(AppActions.showLoader());
    downloadProjectsReport({
      ...resourcePoolFilter,
      allocationStartDate: resourcePoolFilter.allocationStartDate ? moment(resourcePoolFilter.allocationStartDate).format('MM/DD/YYYY') : moment(new Date()).format('MM/DD/YYYY'),
      allocationEndDate: resourcePoolFilter.allocationEndDate ? moment(resourcePoolFilter.allocationEndDate).format('MM/DD/YYYY') :  moment(new Date()).format('MM/DD/YYYY'),
      offset: 0,
      limiy: 0
    })
    .then((res) => {
      if(res.status === 200) {
        fileDownload(res.data, 'projects-report.xlsx');
      }else{
        setSnackBarMessage('No data found.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      }
      dispatch(AppActions.hideLoader());
    })
    .catch((err) => {
      setSnackBarMessage('No data found.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      dispatch(AppActions.hideLoader());
    });
  }

  return (
    <div className='resource-pool'>
      <Card>
        <Grid sx={{ px: 2, py: 2 }} item xs={12} display='flex' justifyContent='space-between'>
          <Button
            id="btn-setToggleFilter"
            sx={{
              mt: { xs: 2, md: 0 },
              color: `${!toggleFilter && 'gray'}`
            }}
            variant="outlined"
            startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
            onClick={() => {
              setToggleFilter(!toggleFilter);
            }}
            style={{ height: '1.75rem', fontSize: '12px' }}
          >
            Filters
          </Button>
          {appUser.Roles.split(',').includes('Super User') &&
            <Button
              sx={{
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              startIcon={<GetAppIcon fontSize="small" />}
              style={{ height: '1.75rem' }}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          }
        </Grid>
        {toggleFilter && (
          <Grid px={2} pb={2} style={{margin: 0}}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'between',
                  flexWrap: 'nowrap',
                  alignItems: 'center'
                }}
              >
                <Grid item xs={3} width='20%'>
                  <ProjectMultiSelect 
                    {...register('project')}
                    value={resourcePoolFilter?.project || []}
                    onChange={(e: any) => {
                      setResourcePoolFilter((prev) => ({
                        ...prev,
                        project: e.target.value
                      }));
                      setValue('project', e.target.value);
                      setIsFilterChanged(true);
                    }}/>
                </Grid>
                <Grid item xs={3} width='20%'>
                  <ResourceMultiSelect 
                    {...register('resource')}
                    value={resourcePoolFilter?.resource || []}
                    onChange={(e: any) => {
                      setResourcePoolFilter((prev) => ({
                        ...prev,
                        resource: e.target.value
                      }));
                      setValue('resource', e.target.value);
                      setIsFilterChanged(true);
                    }}/>
                </Grid>
                <Grid item xs={3} width='20%'>
                  <AllocationRoleMultiSelect
                    {...register('allocationRoles')}
                    value={resourcePoolFilter?.allocationRoles || []}
                    onChange={(e: any) => {
                      setResourcePoolFilter((prev) => ({
                        ...prev,
                        allocationRoles: e.target.value
                      }));
                      setValue('allocationRoles', e.target.value);
                      setIsFilterChanged(true);
                    }}/>
                </Grid>
                <Grid item xs={3} width='20%'>
                  <ResourceTypeSelect 
                    {...register('resourceType')}
                    value={resourcePoolFilter?.resourceType || ''}
                    onChange={(e: any) => {
                      setResourcePoolFilter((prev) => ({
                        ...prev,
                        resourceType: e.target.value
                      }));
                      setValue('resourceType', e.target.value);
                      setIsFilterChanged(true);
                    }}/>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={1}
                style={{
                  justifyContent: 'between',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  marginTop: '5px',
                }}
              >
                <Grid item xs={3} width='25%'>
                  <SingleDatePicker
                    {...register('allocationStartDate')}
                    fullWidth={true}
                    label='Allocation Start Date'
                    value={resourcePoolFilter?.allocationStartDate || null}
                    onChange={(e: any) => {
                      setResourcePoolFilter((prev) => ({
                        ...prev,
                        allocationStartDate: e
                      }));
                      setValue('allocationStartDate', e);
                      setIsFilterChanged(true);
                    }}/>
                </Grid>
                <Grid item xs={3} width='25%'>
                  <SingleDatePicker
                  {...register('allocationEndDate', {
                    validate: value => isEndDateValid(value, startDate) 
                  })}
                  fullWidth={true}
                  disable={resourcePoolFilter?.allocationStartDate === null}
                  minDate={resourcePoolFilter?.allocationStartDate}
                  label='Allocation End Date'
                  value={resourcePoolFilter?.allocationEndDate || null}
                  onChange={(e: any) => {
                    setResourcePoolFilter((prev) => ({
                      ...prev,
                      allocationEndDate: e
                    }));
                    setValue('allocationEndDate', e);
                    setIsFilterChanged(true);
                  }}/>
                  <div style={{fontSize: '9px', color: 'red', position: 'absolute', marginLeft: '15px'}}>
                    {resourcePoolFilter?.allocationStartDate && resourcePoolFilter?.allocationEndDate == null &&
                    "End date is mandatory field"}
                    </div>
                </Grid>
                <Grid
                  item xs={3}
                  width='60%'
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginLeft: 'auto',
                    marginTop: '8px',
                    display: 'flex'
                  }}
                >
                  <Grid
                    item xs={3}
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      marginLeft: 'auto',
                      marginTop: '8px',
                      display: 'flex'
                    }}
                  >
                    <Button
                      sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                      variant="outlined"
                      onClick={resetFilterResult}
                      disabled={isClearDisabled}
                      className={isClearDisabled &&'clear-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                      id="btn-resetFilterResult"
                    >
                      Clear
                    </Button>
                    <Button
                      sx={{ mt: { xs: 2, md: 0 } }}
                      id="btn-onSubmit"
                      variant="outlined"
                      type="submit"
                      disabled={isDisabled}
                      className={isDisabled &&'filter-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                    >
                      Filter
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        )}
        <Divider />
        <TableContainer className="tableContainer" style={{ height: (resources?.length === 0 || !resources) ? '250px' : 'auto' }}>
          <Table style={{ borderCollapse: 'separate', position: 'relative' }} size="small">
            <TableHead>
              <CustomTableRow style={{position: 'sticky', top: 0, zIndex: 1}}>
                <TableHeaderCell content="" smallFont />
                <TableHeaderCell content="Project Name" smallFont />
                <TableHeaderCell content="Client Name" smallFont/>
                <TableHeaderCell content="Resource Name" smallFont/>
                <TableHeaderCell content="Allocation Role" smallFont/>
                <TableHeaderCell width="125px" className='cell-text-wrap-pretty' content="Allocations % (100%,75%,50%,25%)" smallFont />
                <TableHeaderCell width="125px" className='cell-text-wrap-pretty' content="Allocation Type (Billable/Non-Billable)" smallFont />
                <TableHeaderCell content="Allocation Start Date" smallFont />
                <TableHeaderCell content="Allocation End Date" smallFont />
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {resources &&
                resources?.length > 0 &&
                resources.map((project, i) => {
                  return (
                    <Fragment key={project.id}>
                      <TableRow
                        hover
                        key={project.id}
                        className={(expand?.id === project?.id && expand?.expand) ? 'selected-row' : ''}
                      >
                        <TableColumnCell>
                          { project?.resources.length > 1 && 
                            <Button
                              sx={{
                                mt: { xs: 2, md: 0 },
                                fontSize: '11px',
                                fontWeight: 'normal',
                                width: '14px',
                                height: '14px',
                                padding: 0,
                                minWidth: 'unset',
                                borderRadius: '2px',
                                cursor: 'pointer'
                              }}
                              onClick={e => handleExpand(project.id)}
                              variant="contained"
                              startIcon={expand?.id === project?.id && expand?.expand ? <HorizontalRuleIcon fontSize="small" style={{fontSize: '14px'}}/> : 
                              <AddTwoToneIcon fontSize="small" style={{fontSize: '14px'}}/>}
                            />
                          }
                        </TableColumnCell>
                        <TableColumnCell>
                          <Grid>
                            <Typography
                              paddingBottom={project?.resources.length === 0 ? "0" : "0.25rem"}
                              marginBottom={project?.resources.length === 0 ? "0" : "4px"}
                              paddingTop="0.5rem"
                              variant="body2"
                              style={{ fontSize: '11px' }}
                              gutterBottom
                              noWrap
                            >
                              {project.name}
                            </Typography>
                            {project?.resources.length === 0 && 
                              <Typography noWrap style={{fontSize: '9px', color: 'blue'}}>
                                {project?.clientName === null ? '(No resources are allocated for this project)' : 
                                '(No resources are allocated for this project for the filtered date rage)'}</Typography>
                            }
                          </Grid>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom={project?.resources.length === 0 ? "0" : "0.25rem"}
                            marginBottom={project?.resources.length === 0 ? "0" : "4px"}
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            gutterBottom
                            noWrap
                          >
                            {project.clientName}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {getResource(project?.resources)}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {getRole(project?.resources)}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell className='cell-text-wrap-pretty'>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {project?.resources.length > 1 ? 
                              getProjectTotalAllocation(project?.resources) :  
                              project?.resources.length === 1 ? project?.resources[0].allocation + "%" : "-"}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell className='cell-text-wrap-pretty'>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {project?.resources.length > 1 ? project?.resources.filter((value, index) =>value.resourceType === "Billable").length + " / " +
                            project?.resources.filter((value, index) =>value.resourceType === "Non-Billable").length : project?.resources.length === 1 ? 
                            project?.resources[0].resourceType : "-"}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {getMinimumDate(project?.resources)}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            variant="body2"
                            style={{ fontSize: '11px' }}
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {getMaximumDate(project?.resources)}
                          </Typography>
                        </TableColumnCell>
                      </TableRow>
                      {
                        expand?.id === project?.id && expand?.expand && project?.resources.length > 0 && project?.resources.map((resource, i) => {
                          return (
                            <TableRow
                              hover
                              key={resource.code}
                              className={project?.resources.length - 1 === i ? 'project-row project-final-row' : 
                              i === 0 ? 'project-row project-first-row' : 'project-row'}
                            >
                              <TableColumnCell></TableColumnCell>
                              <TableColumnCell></TableColumnCell>
                              <TableColumnCell></TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {resource.name}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {resource.role}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell className='cell-text-wrap-pretty'>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {resource.allocation + "%"}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell className='cell-text-wrap-pretty'>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {resource.resourceType}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {moment(resource?.allocationStartDate?.split(` `)[0]).format(
                                    'YYYY-MM-DD'
                                  ) || '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {moment(resource?.allocationEndDate?.split(` `)[0]).format(
                                    'YYYY-MM-DD'
                                  ) || '-'}
                                </Typography>
                              </TableColumnCell>
                            </TableRow>
                          );
                        })
                      }
                    </Fragment>
                  );
                })
              }
            </TableBody>
          </Table>
          {(resources?.length === 0 || !resources)&& (
            <NoRecord/>
          )}
        </TableContainer>
        <Box p={1} ref={pagination_wrapper_ref} sx={{borderTop: '1px solid #eee'}}>
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={total ? total : 0}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={resourcePoolFilter.offset}
            rowsPerPage={resourcePoolFilter.limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </div>
  );
};

export default ProjectTable;